import type { ReactNode } from 'react';
import type { ContentBlock } from '../types';

export const IMG_SRC_RE = /^(https?:\/\/[^\s]+|\/(content|images|assets)\/[^\s]+)$/i;

function coerceBlocks(raw: ContentBlock[] | undefined): ContentBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: ContentBlock[] = [];
  for (const b of raw) {
    if (!b || typeof b !== 'object') continue;
    const o = b as Record<string, unknown>;
    if (o.kind === 'heading' || o.kind === 'para' || o.kind === 'callout') {
      if (typeof o.text === 'string' && o.text.trim()) out.push({ kind: o.kind, text: o.text });
    } else if (o.kind === 'bullets') {
      const items = Array.isArray(o.items)
        ? o.items.filter((i): i is string => typeof i === 'string' && i.trim().length > 0)
        : [];
      if (items.length) out.push({ kind: 'bullets', items });
    } else if (o.kind === 'image') {
      if (typeof o.src === 'string' && IMG_SRC_RE.test(o.src) && typeof o.alt === 'string' && o.alt.trim()) {
        out.push({
          kind: 'image',
          src: o.src,
          alt: o.alt,
          ...(typeof o.caption === 'string' && o.caption.trim() ? { caption: o.caption } : {}),
        });
      }
    } else if (o.kind === 'path') {
      out.push({ kind: 'path' });
    } else if (o.kind === 'steps' || o.kind === 'cards') {
      const items = Array.isArray(o.items)
        ? o.items
            .filter((i): i is { title: string; text: string } =>
              !!i && typeof i === 'object' && typeof (i as { title?: unknown }).title === 'string' &&
              typeof (i as { text?: unknown }).text === 'string' && !!(i as { title: string }).title.trim())
            .map((i) => ({ title: i.title, text: i.text }))
        : [];
      if (items.length) out.push({ kind: o.kind, items });
    }
  }
  return out;
}

/** A reading step's body. `path` is what a `path` block shows: the learner's live "Your path" card. */
export default function ReadingBody({ content, path }: { content?: ContentBlock[]; path?: ReactNode }) {
  const blocks = coerceBlocks(content);
  if (blocks.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-rule p-6 text-center text-sm text-ink-400 dark:border-clay-rule">
        This lesson’s content is coming soon. You can still mark it done.
      </div>
    );
  }
  return (
    <div className="mt-4 flex flex-col gap-3 text-[15px] leading-relaxed text-ink-700 dark:text-paper-200">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'heading':
            return (
              <h2 key={i} className="mt-2 text-base font-semibold text-ink-900 dark:text-paper-50">
                {b.text}
              </h2>
            );
          case 'para':
            return <p key={i}>{b.text}</p>;
          case 'bullets':
            return (
              <ul key={i} className="ml-5 list-disc space-y-1 marker:text-terracotta-400">
                {b.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            );
          case 'callout':
            return (
              <aside
                key={i}
                role="note"
                className="rounded-xl border-l-4 border-verdant-500 bg-verdant-200/25 px-4 py-3 text-sm text-ink-700 dark:bg-verdant-700/15 dark:text-paper-200"
              >
                {b.text}
              </aside>
            );
          case 'image':
            return (
              <figure key={i} className="overflow-hidden rounded-xl border border-rule dark:border-clay-rule">
                <img
                  src={b.src}
                  alt={b.alt}
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="w-full"
                />
                {b.caption && (
                  <figcaption className="px-3 py-2 text-xs text-ink-400 dark:text-paper-400">
                    {b.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'path':
            return path ? <div key={i}>{path}</div> : null;
          case 'steps':
            return (
              <ol key={i} className="my-1 space-y-0">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-terracotta-500 text-xs font-semibold text-white">
                        {j + 1}
                      </span>
                      {j < b.items.length - 1 && <span className="w-px flex-1 bg-terracotta-200 dark:bg-clay-rule" />}
                    </div>
                    <div className="pb-4">
                      <p className="font-semibold text-ink-900 dark:text-paper-50">{it.title}</p>
                      {it.text && <p className="text-sm text-ink-600 dark:text-paper-300">{it.text}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            );
          case 'cards':
            return (
              <div key={i} className="grid gap-3 sm:grid-cols-2">
                {b.items.map((it, j) => (
                  <div
                    key={j}
                    className="rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider text-terracotta-500">{it.title}</p>
                    {it.text && <p className="mt-1 text-sm text-ink-700 dark:text-paper-200">{it.text}</p>}
                  </div>
                ))}
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
